/** Shared utilities for post-office headless API hooks. */

import type { SerializableRecord } from '@atlassian/post-office-placement-data/types';

import type {
	PlacementDataEndpointData,
	PlacementMessage,
	PlacementMessageDiscriminant,
} from './usePlacementData';

// Error classes

/** Thrown when one or more messages fail the structural validation check. */
export class PlacementValidationError extends Error {
	override readonly name = 'PlacementValidationError';
	readonly placementId: string;
	readonly failedCount: number;

	constructor({ placementId, failedCount }: { placementId: string; failedCount: number }) {
		super('One or more messages failed structural validation');
		this.placementId = placementId;
		this.failedCount = failedCount;
		Object.setPrototypeOf(this, new.target.prototype);
	}
}

/** Thrown by `useMessageData` when more than one instance is found for the template. Use `useMessageInstances` to receive all. */
export class MultipleMessageInstancesError extends Error {
	override readonly name = 'MultipleMessageInstancesError';
	readonly placementId: string;
	readonly messageTemplateId: string;
	readonly instanceCount: number;

	constructor({
		placementId,
		messageTemplateId,
		instanceCount,
	}: {
		placementId: string;
		messageTemplateId: string;
		instanceCount: number;
	}) {
		super(
			'Multiple message instances found; the hook is in an unexpected state and no message will be returned. Use useMessageInstances if multiple instances are expected.',
		);
		this.placementId = placementId;
		this.messageTemplateId = messageTemplateId;
		this.instanceCount = instanceCount;
		Object.setPrototypeOf(this, new.target.prototype);
	}
}

/** Thrown by `.match()` when any item at the location key is not a valid `PlacementMessage`. */
export class PlacementLocationError extends Error {
	override readonly name = 'PlacementLocationError';
	readonly locationKey: string;
	readonly invalidCount: number;

	constructor({ locationKey, invalidCount }: { locationKey: string; invalidCount: number }) {
		super('All items at the message location must be valid PlacementMessage objects');
		this.locationKey = locationKey;
		this.invalidCount = invalidCount;
		Object.setPrototypeOf(this, new.target.prototype);
	}
}

// Internal helpers

/** Structural type guard for `PlacementMessage`. */
export const isPlacementMessage = (
	item: unknown,
): item is PlacementMessage<PlacementMessageDiscriminant> => {
	if (typeof item !== 'object' || item === null) {
		return false;
	}
	const msg = item as Record<string, unknown>;
	return (
		typeof msg.messageTemplateId === 'string' &&
		typeof msg.messageInstanceId === 'string' &&
		typeof msg.context === 'object' &&
		msg.context !== null
	);
};

/** Normalizes unknown thrown values into an Error instance for hook error surfaces. */
export const toHookError = (thrown: unknown, fallbackMessage = 'Hook callback threw'): Error => {
	if (thrown instanceof Error) {
		return thrown;
	}
	if (typeof thrown === 'string') {
		return new Error(thrown);
	}

	return new Error(fallbackMessage);
};

// Match Builder

/** @internal — used to tag match-builder objects in place of `instanceof`. */
const MATCH_BUILDER_TAG = Symbol('MatchBuilder');

type MessageContextPredicate<TMessage extends PlacementMessageDiscriminant> = (
	context: TMessage['context'],
) => boolean;

type DataPredicate<
	TMessage extends PlacementMessageDiscriminant,
	TExtension extends SerializableRecord,
> = (data: PlacementDataEndpointData<TMessage, TExtension>) => boolean;

/**
 * The fluent builder chain returned after `.messageLocation()` has been called (or when predicates
 * are added directly without calling `.messageLocation()`).
 *
 * `messageLocation` is intentionally absent — it may only be called once, as the first step.
 * All predicates are AND'd together.
 *
 * Can be returned from `match` without calling `.match(data)` — the hook calls it automatically.
 * Alternatively, call `.match(data)` manually to resolve the filtered array yourself.
 */
export type MatchBuilderChain<
	TMessage extends PlacementMessageDiscriminant = PlacementMessageDiscriminant,
	TExtension extends SerializableRecord = Record<never, never>,
> = {
	/** Add an AND predicate on the message's `context` field. */
	messageContext(
		predicate: MessageContextPredicate<TMessage>,
	): MatchBuilderChain<TMessage, TExtension>;
	/** Add an AND predicate on the raw API data object. */
	custom(predicate: DataPredicate<TMessage, TExtension>): MatchBuilderChain<TMessage, TExtension>;
	/** Execute the built filter against `data` and return the matching messages. */
	match(data: PlacementDataEndpointData<TMessage, TExtension>): Array<PlacementMessage<TMessage>>;
};

/**
 * The initial builder returned by `createMatchBuilder`.
 * Extends `MatchBuilderChain` with `messageLocation`, which must be called first and at most once.
 * After calling `.messageLocation()` the returned chain no longer exposes `messageLocation`.
 */
export type MatchBuilderInitialChain<
	TMessage extends PlacementMessageDiscriminant = PlacementMessageDiscriminant,
	TExtension extends SerializableRecord = Record<never, never>,
> = MatchBuilderChain<TMessage, TExtension> & {
	/** Select which top-level key on the API response contains the message array. Defaults to `'messages'`. Must be called first and at most once. */
	messageLocation(
		key: keyof PlacementDataEndpointData<TMessage, TExtension> & string,
	): MatchBuilderChain<TMessage, TExtension>;
};

/**
 * @internal — alias for `MatchBuilderChain` used by hook internals.
 */
export type MatchBuilder<
	TMessage extends PlacementMessageDiscriminant = PlacementMessageDiscriminant,
	TExtension extends SerializableRecord = Record<never, never>,
> = MatchBuilderChain<TMessage, TExtension>;

/** @internal — creates a builder with the given accumulated state (location already resolved). */
const makeMatchBuilder = <
	TMessage extends PlacementMessageDiscriminant,
	TExtension extends SerializableRecord,
>(
	locationKey: string,
	contextPredicates: ReadonlyArray<MessageContextPredicate<TMessage>>,
	dataPredicates: ReadonlyArray<DataPredicate<TMessage, TExtension>>,
): MatchBuilder<TMessage, TExtension> => {
	const builder: MatchBuilder<TMessage, TExtension> = {
		messageContext(predicate) {
			return makeMatchBuilder(locationKey, [...contextPredicates, predicate], dataPredicates);
		},
		custom(predicate) {
			return makeMatchBuilder(locationKey, contextPredicates, [...dataPredicates, predicate]);
		},
		match(data) {
			if (dataPredicates.some((p) => !p(data))) {
				return [];
			}

			const raw: unknown = (data as Record<string, unknown>)[locationKey] ?? [];
			if (!Array.isArray(raw)) {
				throw new PlacementLocationError({ locationKey, invalidCount: 1 });
			}
			const items = raw;

			const invalidCount = items.filter((item) => !isPlacementMessage(item)).length;
			if (invalidCount > 0) {
				throw new PlacementLocationError({ locationKey, invalidCount });
			}

			const candidates = items as Array<PlacementMessage<TMessage>>;

			return candidates.filter((message) =>
				contextPredicates.every((p) => p(message.context as TMessage['context'])),
			);
		},
	};

	// Tag the builder with a non-enumerable symbol so isMatchBuilder can identify it without
	// relying on instanceof (which would break across module boundaries or after minification).
	Object.defineProperty(builder, MATCH_BUILDER_TAG, { value: true, enumerable: false });

	return builder;
};

/** @internal — creates the initial builder with `messageLocation` available. */
const makeInitialMatchBuilder = <
	TMessage extends PlacementMessageDiscriminant,
	TExtension extends SerializableRecord,
>(): MatchBuilderInitialChain<TMessage, TExtension> => {
	const initial: MatchBuilderInitialChain<TMessage, TExtension> = {
		messageLocation(key) {
			return makeMatchBuilder<TMessage, TExtension>(key, [], []);
		},
		messageContext(predicate) {
			return makeMatchBuilder<TMessage, TExtension>('messages', [predicate], []);
		},
		custom(predicate) {
			return makeMatchBuilder<TMessage, TExtension>('messages', [], [predicate]);
		},
		match(data) {
			return makeMatchBuilder<TMessage, TExtension>('messages', [], []).match(data);
		},
	};

	Object.defineProperty(initial, MATCH_BUILDER_TAG, { value: true, enumerable: false });

	return initial;
};

/** Returns `true` if `value` is a match-builder object created by `createMatchBuilder`. */
export const isMatchBuilder = (value: unknown): value is MatchBuilder => {
	return (
		typeof value === 'object' &&
		value !== null &&
		(value as Record<symbol, unknown>)[MATCH_BUILDER_TAG] === true
	);
};

/** Creates a fluent match-builder. Call `.messageLocation()` first to select a non-default key. */
export const createMatchBuilder = <
	TMessage extends PlacementMessageDiscriminant = PlacementMessageDiscriminant,
	TExtension extends SerializableRecord = Record<never, never>,
>(): MatchBuilderInitialChain<TMessage, TExtension> =>
	makeInitialMatchBuilder<TMessage, TExtension>();
